<template>
	<div class="eventAvailabilityCard-container appWhite">

		<!--Event Details | Shift Details | Info Icon |  Action Buttons -->
		<div class="d-flex">

			<!--Event Date/Time-->
			<div class="eventAvailabilityCard-dateTime-container" :class="$vuetify.breakpoint.width < 439 && 'mb-4'">

				<!--Date-->
				<div class="eventAvailabilityCard-dateTime-date">
					<app-text>{{ computedDate.dayName }}</app-text>
					<app-text size="normal-bold">{{ computedDate.dayNumber }}</app-text>
					<app-text>{{ computedDate.monthName }}</app-text>
				</div>

				<!--KO Time-->
				<app-text class="eventAvailabilityCard-dateTime-KO text-center" color="greyD" size="small">
					KO @ {{ eventData.eventStartTime }}
				</app-text>

			</div>

			<!--Event Shift Details | Info Icon | Action Buttons-->
			<div class="eventAvailabilityCard-shiftDetails-container">

				<!--Event Shift Details | Info Icon-->
				<div class="d-flex align-center">

					<!--Event Name | Shift Details-->
					<div class="d-flex flex-column justify-space-between">

						<!--Event Name-->
						<app-text color="primary" size="normal-bold">{{ eventData.eventName }}</app-text>

						<!--Team Name-->
						<app-text class="my-1" size="normal-bold">{{ teamData.teamName }}</app-text>

						<!--Shift Details-->
						<app-text color="grey" size="small">
							Start at
							<strong>{{ computedShiftDetails.startTime }}</strong>
							for a
							<strong>{{ computedShiftDetails.shiftLength }}</strong>
							shift
						</app-text>

					</div>

					<v-spacer/>

					<!--Info Icon-->
					<app-icon @click.native="openStaffInstructionsDialog" icon="info" size="48"/>

					<!--Team Notes-->
					<div v-if="['Steward-Supervisor', 'Steward-Deputy'].includes(MIX_getCurrentUser().userLevel)"
						 @click="openTeamNotesDialog"
						 class="d-flex align-center justify-center ml-2"
						 style="border: 3px solid grey; border-radius: 50%; width: 40px; height: 40px">
						<app-text size="medium">T</app-text>
					</div>

				</div>

				<!--Action Buttons-->
				<div class="d-flex mt-1">

					<!--No-->
					<app-btn @click.native="handleAvailabilitySelection('Unavailable')"
							 :block="true"
							 class="pr-2"
							 :color="eventUserData?.eventUserStatus === 'Unavailable' ? 'grey3' : 'grey'"
							 :disabled="disabled"
							 label="No"
							 style="width: 100%"/>

					<!--Yes-->
					<app-btn @click.native="handleAvailabilitySelection('Available')"
							 :block="true"
							 class="pl-2"
							 :color="['Available', 'Confirmed', 'Reserved', 'Not Required'].includes(eventUserData?.eventUserStatus) ? 'green' : 'grey'"
							 :disabled="disabled"
							 label="Yes"
							 style="width: 100%"/>

				</div>

			</div>

		</div>

		<!--See Team Availability Button - Supervisor Only-->
		<app-btn v-if="!disabled && ['Steward-Supervisor', 'Steward-Deputy'].includes(MIX_getCurrentUser().userLevel)"
				 @click.native="$emit('emitShowTeamAvailability', {eventData: eventData, eventsView: 'Status Selection', teamData: teamData})"
				 :block="true" color="primary mt-4"
				 :disabled="disabled"
				 icon="teams"
				 label="See Team Availability"/>

		<!--Status Information-->
		<div v-if="computedStatusInformation.statusTitle" class="text-center mt-4">

			<app-text :color="computedStatusInformation.statusColor" size="normal-bold">
				{{ computedStatusInformation.statusTitle }}
			</app-text>
			<app-text size="small">{{ computedStatusInformation.statusInformation }}</app-text>

		</div>

		<!--Dialogs ------------------------------------------------------------------------------------------------ -->

		<!--Staff Information Dialog-->
		<app-dialog :is-visible="isStaffInstructionsDialogVisible">
			<event-availability-card-staff-instructions-dialog
				@emitCloseEventAvailabilityCardStaffInstructionsDialog="emittedCloseEventAvailabilityCardStaffInstructionsDialog"
				:eventData="eventData"/>
		</app-dialog>

		<!--Status Selection Confirmation Dialog-->
		<app-dialog :is-visible="isStatusSelectionConfirmationDialogVisible" dialogTitle="Update Status">
			<event-availability-card-status-selection-confirmation-dialog
				@emitCloseEventAvailabilityCardStatusSelectionConfirmationDialog="emittedCloseEventAvailabilityCardStatusSelectionConfirmationDialog"
				@emitEventAvailabilityCardStatusSelectionConfirmation="emittedEventAvailabilityCardStatusSelectionConfirmation"
				:eventUserData="eventUserData"
				:statusSelection="statusSelection"/>
		</app-dialog>

		<!--Team Notes Dialog-->
		<app-dialog :is-visible="isTeamNotesDialogVisible" dialogTitle="Team Notes">
			<event-team-notes-dialog @emitCloseTeamNotesDialog="closeTeamNotesDialog"
									 :teamNotes="eventTeamData.eventTeamNotes"/>
		</app-dialog>

	</div>
</template>

<script>
import EventAvailabilityCardStatusSelectionConfirmationDialog
	from "@/views/events/steward/eventAvailabilityCard/eventAvailabilityCardStatusSelectionConfirmationDialog/EventAvailabilityCardStatusSelectionConfirmationDialog";
import EventAvailabilityCardStaffInstructionsDialog
	from "@/views/events/steward/eventAvailabilityCard/eventAvailabilityCardStaffInstructionsDialog/EventAvailabilityCardStaffInstructionsDialog";
import EventTeamNotesDialog
	from "@/views/events/steward/eventAvailabilityCard/eventTeamNotesDialog/EventTeamNotesDialog";

export default {

	name: "EventAvailabilityCard",

	components: {
		EventTeamNotesDialog,
		EventAvailabilityCardStaffInstructionsDialog, EventAvailabilityCardStatusSelectionConfirmationDialog
	},

	props: ['disabled', 'eventData', 'eventTeamData', 'eventUserData', 'teamData'],

	data: () => ({
		isStaffInstructionsDialogVisible: false,
		isStatusSelectionConfirmationDialogVisible: false,
		isTeamNotesDialogVisible: false,
		statusSelection: '',
	}),

	computed: {

		/**
		 * Computed Date
		 *
		 * Return the date in fragments for the DateTime box.
		 *
		 * @returns {{dayName: string, dayNumber: number, monthName: string}} fragments of the date
		 */
		computedDate() {
			const t = this
			const EVENT_DATE = t.$props.eventData.eventDate

			let dayName = new Date(EVENT_DATE).toLocaleDateString('en-GB', {weekday: 'short'})
			let dayNumber = new Date(EVENT_DATE).getDate()
			let monthName = new Date(EVENT_DATE).toLocaleDateString('en-GB', {month: 'short'})

			return {dayName, dayNumber, monthName}
		},

		/**
		 * Computed Shift Details
		 *
		 * Return the Shift Start Time and Length for the event by userLevel.
		 *
		 * @returns {{startTime: string, shiftLength: string}} shift details
		 */
		computedShiftDetails() {
			const t = this
			const CURRENT_USER_DATA = t.MIX_getCurrentUser()
			const EVENT_DATA = t.$props.eventData
			const EVENT_TEAM_DATA = t.$props.eventTeamData
			const EVENT_USER_DATA = t.$props.eventUserData
			let startTime
			let finishTime
			let shiftLength

			// Convert time string to Date object
			const timeStringToDate = timeString => {
				const [hours, minutes] = timeString.split(':').map(Number)
				const now = new Date()
				now.setHours(hours, minutes, 0, 0)
				return now
			}

			// Calculate shift length in hours and minutes
			const calculateShiftLength = (start, finish) => {
				const START_DATE = timeStringToDate(start)
				const FINISH_DATE = timeStringToDate(finish)
				const DIFF_MINUTES = FINISH_DATE - START_DATE
				const DIFF_HOURS = Math.floor(DIFF_MINUTES / 3600000)
				const REMAINING_MINUTES = Math.floor((DIFF_MINUTES % 3600000) / 60000)
				return `${DIFF_HOURS}h ${REMAINING_MINUTES}m`
			}

			// Supervisor & Deputy
			if (['Steward-Supervisor', 'Steward-Deputy'].includes(CURRENT_USER_DATA.userLevel)) {
				startTime = EVENT_USER_DATA?.eventUserStartTime || EVENT_TEAM_DATA?.eventTeamSupervisorsStartTime || EVENT_DATA.eventSupervisorsStartTime
				finishTime = EVENT_USER_DATA?.eventUserFinishTime || EVENT_TEAM_DATA?.eventTeamSupervisorsFinishTime || EVENT_DATA.eventSupervisorsFinishTime
			}

			// Steward
			if (['Steward-User'].includes(CURRENT_USER_DATA.userLevel)) {
				startTime = EVENT_USER_DATA?.eventUserStartTime || EVENT_TEAM_DATA?.eventTeamStewardsStartTime || EVENT_DATA.eventStewardsStartTime
				finishTime = EVENT_USER_DATA?.eventUserFinishTime || EVENT_TEAM_DATA?.eventTeamStewardsFinishTime || EVENT_DATA.eventStewardsFinishTime
			}

			if (startTime && finishTime) shiftLength = calculateShiftLength(startTime, finishTime)
			else shiftLength = 'N/A'

			return {startTime, shiftLength}
		},

		/**
		 * Computed Status Information
		 *
		 * Create and return the required status information for rendering.
		 *
		 * @returns {{statusInformation: string, statusColor: string, statusTitle: string}}
		 */
		computedStatusInformation() {
			const t = this
			const EVENT_USER_STATUS = t.$props.eventUserData?.eventUserStatus
			let statusColor
			let statusTitle
			let statusInformation

			if (EVENT_USER_STATUS === 'Available') {
				statusColor = 'orange'
				statusInformation = 'You will be notified if you are required.'
				statusTitle = 'Available'
			}

			if (EVENT_USER_STATUS === 'Confirmed') {
				statusColor = 'green'
				statusInformation = `When you arrive at the Stadium on event day, you must SWAPP in with a QR Code on the gate, and then see your Supervisor to get Signed In.`
				statusTitle = 'Confirmed'
			}

			if (EVENT_USER_STATUS === 'Reserved') {
				statusColor = 'blue'
				statusInformation = 'You are on the Reserve list and will be notified if you are required.'
				statusTitle = 'Reserved'
			}

			if (EVENT_USER_STATUS === 'Not Required') {
				statusColor = 'red'
				statusInformation = 'You are not required for this Event, but will be notified if anything changes.'
				statusTitle = 'Not Required'
			}

			return {statusColor, statusInformation, statusTitle}
		},

	},

	methods: {

		/**
		 * Close Staff Instructions Dialog
		 *
		 * Close the dialog box.
		 */
		closeStaffInstructionsDialog() {
			const t = this

			t.isStaffInstructionsDialogVisible = false
		},

		/**
		 * Close Status Selection Confirmation Dialog
		 *
		 * Close the dialog box.
		 */
		closeStatusSelectionConfirmationDialog() {
			const t = this

			t.isStatusSelectionConfirmationDialogVisible = false
		},

		/**
		 * Close Team Notes Dialog
		 *
		 * Close the dialog box.
		 */
		closeTeamNotesDialog() {
			const t = this

			t.isTeamNotesDialogVisible = false
		},

		/**
		 * Emitted Close Event Availability Card Staff Instructions Dialog
		 *
		 * Call to close the dialog box.
		 */
		emittedCloseEventAvailabilityCardStaffInstructionsDialog() {
			const t = this

			t.closeStaffInstructionsDialog()
		},

		/**
		 * Emitted Close Event Availability Card Status Selection Confirmation Dialog
		 *
		 * Call to close the dialog.
		 */
		emittedCloseEventAvailabilityCardStatusSelectionConfirmationDialog() {
			const t = this

			t.closeStatusSelectionConfirmationDialog()
		},

		/**
		 * Emitted Event Availability Card Status Selection Confirmation
		 *
		 * Call to update the User's EventUser status, and close the dialog.
		 */
		emittedEventAvailabilityCardStatusSelectionConfirmation() {
			const t = this

			t.updateAvailability()
			t.closeStatusSelectionConfirmationDialog()
		},

		/**
		 * Handle Availability Selection
		 *
		 * Handle the button selection by setting the status to the state and opening the confirmation dialog.
		 *
		 * @param statusSelection - the selected status
		 */
		handleAvailabilitySelection(statusSelection) {
			const t = this

			if (t.$props.disabled) return

			t.statusSelection = statusSelection
			t.isStatusSelectionConfirmationDialogVisible = true
		},

		/**
		 * Open Staff Instructions Dialog
		 *
		 * Open the dialog box.
		 */
		openStaffInstructionsDialog() {
			const t = this

			t.isStaffInstructionsDialogVisible = true
		},

		/**
		 * Open Team Notes Dialog
		 *
		 * Open the dialog box.
		 */
		openTeamNotesDialog() {
			const t = this

			t.isTeamNotesDialogVisible = true
		},

		/**
		 * Update Availability
		 *
		 * Update the current User's EventUser status for this Event.
		 *
		 * @returns {Promise<void>}
		 */
		async updateAvailability() {
			const t = this
			const EVENT_USER_DATA = t.$props.eventUserData
			const STATUS_SELECTION = t.statusSelection

			EVENT_USER_DATA.eventUserStatus = STATUS_SELECTION

			const RESPONSE = await t.MIX_redis_update('eventUser', EVENT_USER_DATA.entityId, EVENT_USER_DATA)

			// Handle and errors
			if (RESPONSE.hasErrors) {
				console.error('Error updating EventUser: ', RESPONSE.error)
				return
			}

		},

	},

}
</script>

<style scoped>
.eventAvailabilityCard-container {
	border-radius: 16px;
	margin-top: 16px;
	max-width: 600px;
	padding: 16px;
}

.eventAvailabilityCard-dateTime-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;

	border-radius: 8px;
	width: 128px;
}

.eventAvailabilityCard-dateTime-date {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: space-between;

	background-color: var(--v-greyD-base);
	border-radius: 8px 8px 0 0;
	padding: 8px;
	width: 100%;
	min-height: calc(100% - 32px);
}

.eventAvailabilityCard-dateTime-KO {
	background-color: var(--v-grey3-base);
	border-radius: 0 0 8px 8px;
	padding: 8px;
	width: 100%;
}

.eventAvailabilityCard-shiftDetails-container {
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	margin-left: 16px;
	width: 100%;
}
</style>
